import React, { useRef, useState, useEffect, useContext } from "react"
import styled from "styled-components"
import gsap from "gsap"

import { useMedia } from "utils/hooks"

import colors from "assets/styles/colors"
import text, { responsiveText } from "assets/styles/text"
import media from "assets/styles/media"

import BGGrid from "components/uIElements/BGGrid"
import { ScreenContext } from "components/Providers"
import { isBrowser } from "utils/functions"

const Hero: React.FC = () => {
  const left = useRef<HTMLDivElement>(null)
  const right = useRef<HTMLDivElement>(null)
  const middle = useRef<HTMLDivElement>(null)
  const backing = useRef<HTMLSpanElement>(null)
  const since = useRef<HTMLSpanElement>(null)
  const date = useRef<HTMLSpanElement>(null)
  const scroll = useRef<HTMLDivElement>(null)
  const [wrapper, setWrapper] = useState<HTMLElement | null>(null)
  const { tablet } = useContext(ScreenContext)
  const scrollValue = useMedia("50vh", "50vh", "16vh", "35vh")
  const sinceFontSize = useMedia("24.805vw", "24.805vw", "42.19vw", "41.07vw")
  const dateFontSize = useMedia("36.698vw", "36.698vw", "66.5vw", "57.6vw")
  const sinceBottom = useMedia(
    "20vh",
    "16vh",
    "16vw",
    `${isBrowser() && (window.innerWidth / 100) * 25}px`
  )
  const sinceLeft = useMedia("8.75vw", "8.75vw", "0", "0")
  const dateBottom = useMedia(
    "30vh",
    "26vh",
    "20vw",
    `${isBrowser() && (window.innerWidth / 100) * 25}px`
  )
  const endValue = useMedia(
    `bottom bottom`,
    `bottom bottom`,
    `bottom bottom`,
    `bottom bottom`
  )

  useEffect(() => {
    if (wrapper) {
      const tl1 = gsap.timeline({
        scrollTrigger: {
          trigger: wrapper,
          scrub: true,
          scroller: "#___gatsby",
          start: "top top",
          end: endValue,
          // markers: true,
        },
        ease: "customOne",
      })

      tl1.to(
        backing.current,
        {
          opacity: 0,
        },
        0
      )

      tl1.to(
        since.current,
        {
          fontSize: sinceFontSize,
          y: sinceBottom,
          left: sinceLeft,
          ease: "linear",
        },
        0
      )

      tl1.to(
        date.current,
        {
          fontSize: dateFontSize,
          y: dateBottom,
          ease: "linear",
        },
        0
      )

      tl1.to(
        scroll.current,
        {
          y: scrollValue,
          color: colors.blue400,
          ease: "linear",
        },
        0
      )

      const tl2 = gsap.timeline({
        scrollTrigger: {
          trigger: wrapper,
          scrub: true,
          scroller: "#___gatsby",
          start: "top top",
          end: endValue,
        },
      })

      tl2.to(
        left.current,
        {
          scaleY: 2,
          y: "-3.472vw",
        },
        0
      )

      tl2.to(
        right.current,
        {
          scaleY: 3,
          y: "-3.472vw",
        },
        0
      )

      return () => {
        tl1.kill()
        tl2.kill()
      }
    }
  }, [
    wrapper,
    scrollValue,
    dateFontSize,
    dateBottom,
    sinceFontSize,
    endValue,
    sinceBottom,
    sinceLeft,
  ])

  const lineConfig = {
    sectionHeight: "100%",
    animationClass: "hero",
    sectionRef: wrapper,
    filledColumns: [
      {
        column: 0,
        height: useMedia("5.56vw", "5.56vw", "13.67vw", "20vw") ?? "5.56v",
        topOffset:
          useMedia("47.36vw", "47.36vw", "73.24vw", "133.87vw") ?? "47.36vw",
        color: colors.lightBlueGradient,
        zIndex: "5",
        ref: left,
      },
      {
        column: useMedia(3, 3, 3, 1) ?? 3,
        height: useMedia("29.72vw", "29.72vw", "21.48vw", "40vw") ?? "29.72vw",
        topOffset: useMedia("5.906vw", "5.906vw", "9.77vw", "20vw"),
        color: colors.lightBlueGradient,
        zIndex: "5",
        ref: middle,
      },
      {
        column: useMedia(8, 8, 3, 3) ?? 8,
        height: useMedia("13.89vw", "13.89vw", "19.531vw", "40vw") ?? "13.89vw",
        topOffset:
          useMedia("36.32vw", "36.32vw", "calc(100vh - 25.98vw)", "146.13vw") ??
          "36.32vw",
        color: colors.lightBlueGradient,
        ref: right,
      },
    ],
  }

  return (
    <Wrapper
      id="home-Hero"
      ref={ref => setWrapper(ref)}
      data-scroll
      data-scroll-section
    >
      <BGGrid lineConfig={lineConfig} />

      <HeroText>
        <Backing ref={backing}>Backing {tablet && <br />} the best</Backing>
        &nbsp;
        {tablet && <br />}
        <Since ref={since}>since</Since>
        <br />
        <Date ref={date}>1994</Date>
      </HeroText>

      <ScrollInvite ref={scroll}>scroll →</ScrollInvite>
    </Wrapper>
  )
}

export default Hero

const Wrapper = styled.section`
  position: relative;
  background: ${colors.white};
  width: 100%;

  padding-top: 11.61vw;
  height: 115vh;

  ${media.fullWidth} {
    padding-top: 8vw;
    height: 150vh;
  }
  ${media.tablet} {
    height: 170.4vw;
    padding-top: 21.48vw;
  }
  ${media.mobile} {
    height: 125vh;
    padding-top: 36vw;
  }
`

const HeroText = styled.h1`
  margin-left: 2.569vw;
  position: relative;

  ${text.desktop.h2};
  ${media.tablet} {
    ${text.tablet.h2};
  }
  ${media.mobile} {
    ${text.mobile.h2};
  }
`

const Backing = styled.span``

const Since = styled.span`
  display: inline-block;
  position: relative;
  transform-origin: bottom left;
`

const Date = styled.span`
  ${responsiveText.d1Point5};
  position: relative;
  transform-origin: bottom left;
  display: block;
  left: 0.5vw;
  margin-bottom: -1px;

  ${media.mobile} {
    ${text.mobile.d2Point5};
  }
`

const ScrollInvite = styled.p`
  ${responsiveText.buttonText};

  border-bottom: solid 1px;
  text-transform: uppercase;
  color: ${colors.black800};
  position: absolute;
  transform: rotate(90deg);

  font-size: 1.25vw;
  line-height: 1.25vw;
  left: 86.3vw;
  top: 44.306vw;
  ${media.tablet} {
    ${text.tablet.buttonText};
    top: auto;
    bottom: 13.184vw;
    left: 77.4vw;
  }
  ${media.mobile} {
    ${text.mobile.buttonText};
    top: calc(100vh - 50.18vw);
    left: 75vw;
  }
`
